import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { UserEnvironmentService } from '@y42/environment';
import { combineLatest, distinctUntilChanged, fromEvent, map, of, shareReplay, startWith, tap } from 'rxjs';
import { UserColorScheme } from './user-color-scheme.enum';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UserColorSchemeService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly userEnvironmentService: UserEnvironmentService,
  ) {
    this.applyColorScheme().subscribe();
  }

  private readonly colorScheme$ = this.userEnvironmentService?.colorScheme$ || of(UserColorScheme.System);
  private readonly hasDarkModeColorScheme = window.matchMedia('(prefers-color-scheme: dark)');
  private readonly systemHasDarkMode$ = fromEvent<MediaQueryListEvent>(this.hasDarkModeColorScheme, 'change').pipe(
    map((evt) => evt.matches),
    startWith(this.hasDarkModeColorScheme.matches),
  );

  readonly hasDarkMode$ = combineLatest([this.colorScheme$, this.systemHasDarkMode$]).pipe(
    map(([appTheme, systemHasDarkMode]) => {
      switch (appTheme) {
        case UserColorScheme.Dark:
          return true;
        case UserColorScheme.Light:
          return false;
        case UserColorScheme.System:
        default:
          return systemHasDarkMode;
      }
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private applyColorScheme() {
    return this.hasDarkMode$.pipe(tap((hasDarkMode) => (hasDarkMode ? this.setDarkMode() : this.setLightMode())));
  }

  private setDarkMode(): void {
    this.document.body.classList.remove('light');
    this.document.body.classList.add('dark');
  }

  private setLightMode(): void {
    this.document.body.classList.remove('dark');
    this.document.body.classList.add('light');
  }
}
